import { DateTimeString } from 'types';
import { Lite, PATCHSchema, POSTSchema } from 'types/api';
import { OrgPartSchema } from 'types/clientV2/parts';
import {
  StockLocationSchema,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import {
  HandlingRequestStatus,
  HandlingRequestType,
  LabelTemplate,
  MslOptions,
  PackagingOptions,
  Printer,
  Shipment,
  ShipmentLineLite,
  StockEventType,
  StockLocation,
  StockLotQuerySchema,
  StockLotQuerySchemaLite,
} from 'types/inventory';
import {
  KitRequestLineSchema,
  KitRequestLineSchemaLite,
  KitRequestSchemaLite,
} from 'types/kitting';
import { Org, OrgLite } from 'types/org';
import { Part } from 'types/part';
import {
  PurchaseOrderLineCore,
  PurchaseOrderSchemaClientV2,
} from 'types/procurement';
import { ProductionRunLite } from 'types/production';
import { Team } from 'types/properties';
import { UserLite } from 'types/user';
import {
  ShipmentLineSchemaLite,
  ShipmentSchemaLite,
} from 'types/clientV2/shipments';
import { PurchaseEventLite } from 'types/purchasing';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

// HANDLING REQUESTS

export type HandlingRequestSchema = {
  id: string;
  quant: number | null;
  notes: string | null;
  billable: boolean | null;
  requestType: HandlingRequestType;
  requestTypeDisplay: string;
  package: PackagingOptions | null;
  status: HandlingRequestStatus;
  // eslint-disable-next-line no-restricted-syntax
  cancelled: boolean | null;
  stockLot: StockLotSchemaLite;
  splitStockLot: StockLotSchemaLite | null;
  stockLocation: StockLocationSchemaLite;
  org: Org;
  kitRequestLine: KitRequestLineSchemaLite | null;
  kitRequestIdViaLot: KitRequestSchemaLite['id'] | null;
  lockedAt: DateTimeString | null;
  submittedAt: DateTimeString;
  submittedBy: UserLite | null;
  completedAt: DateTimeString | null;
  completedBy: UserLite | null;
  pickedAt: DateTimeString | null;
  pickedBy: UserLite | null;
  approvedAt: DateTimeString | null;
  approvedBy: UserLite | null;
  // eslint-disable-next-line no-restricted-syntax
  cancelledAt: DateTimeString | null;
  // eslint-disable-next-line no-restricted-syntax
  cancelledBy: UserLite | null;
  startedAt: DateTimeString | null;
  startedBy: UserLite | null;
};

export const NON_REEL_TAPE_PACKAGES = [
  PackagingOptions.TAPE_SINGLE,
  PackagingOptions.TAPE_MULTIPLE,
];
export const TAPE_PACKAGES = [
  PackagingOptions.TAPE_SINGLE,
  PackagingOptions.TAPE_MULTIPLE,
  PackagingOptions.REEL,
];

export type HandlingRequestSchemaLite = Lite<HandlingRequestSchema>;

export type HandlingRequestPOSTSchema = POSTSchema<
  HandlingRequestSchema,
  'requestType' | 'stockLot',
  | 'stockLocation'
  | 'billable'
  | 'package'
  | 'notes'
  | 'quant'
  | 'kitRequestLine'
>;

export type HandlingRequestPOSTSpliceCombineSchema = {
  stockLots: StockLotSchemaLite['id'][];
  requestType: HandlingRequestType.SPLICE | HandlingRequestType.COMBINE;
  billable?: boolean;
  notes?: string;
  kitRequestLine?: KitRequestLineSchema['id'] | null;
};

export type HandlingRequestPOSTPickSchema = {
  ids: HandlingRequestSchema['id'][];
  stockLocation: StockLocationSchema['id'];
};

export type CancelHandlingRequestSchema = {
  handlingRequestIds: HandlingRequestSchema['id'][];
};

// STOCK LOTS

export type StockLotSchema = Omit<StockLotQuerySchema, 'handlingRequests'> & {
  handlingRequests: HandlingRequestSchemaLite[];
};

export type StockLotSchemaLite = Lite<StockLotSchema>;

export type StockLotPATCHSchema = {
  quant?: number;
  externalNotes?: string;
  internalNotes?: string;
  countryOfOrigin?: string;
  dateCode?: string;
  mfgLot?: string;
  auditedAt?: DateTimeString;
  expiresAt?: DateTimeString;
  quarantined?: boolean;
  smtNoPnp?: boolean;
  msl?: MslOptions;
  mslFloorHours?: number;
  tapeSize?: number;
  unitPricePaid?: number | null;
  landedUnitPrice?: number | null;
  package?: PackagingOptions;
  team?: Team['id'] | null;
  v2OrgPart?: OrgPartSchema['id'] | null;
  customProperties?: object;
};

export type BarcodeLabelSchema = {
  id: string;
  labelCode: string;
  needsReview: boolean;
  package: PackagingOptions;
  stockLocation: StockLocationSchemaLite;
  facility: StockLocationSchemaLite;
  stockLot: StockLotSchemaLite | null;
  shipment: ShipmentSchemaLite | null;
  purchase: Lite<PurchaseOrderSchemaClientV2> | null;
  purchaseEvent: PurchaseEventLite | null;
  createdAt: DateTimeString;
  voidedAt: DateTimeString | null;
  voidedBy: UserLite | null;
};

export type BarcodeLabelSchemaLite = Lite<BarcodeLabelSchema>;

export type BarcodeLabelPOSTSchema = POSTSchema<
  BarcodeLabelSchema,
  'package' | 'stockLocation',
  'labelCode' | 'purchase' | 'purchaseEvent' | 'shipment'
>;

export type BarcodeLabelPATCHSchema = PATCHSchema<BarcodeLabelSchema>;

// STOCK EVENTS

export type StockEventSchema = {
  id: string;
  quant?: number | null;
  timestamp: DateTimeString;
  station?: string | null;
  eventType: StockEventType;
  testPassed?: boolean;
  billable?: boolean;
  mslFloorHours?: number;
  internalNotes?: string;
  externalNotes?: string;
  package?: PackagingOptions | null;
  stockLot: StockLotQuerySchemaLite;
  replacedByStockLot?: StockLotQuerySchemaLite | null;
  stockLocation: StockLocationSchemaLite;
  handlingRequest?: HandlingRequestSchemaLite | null;
  shipmentLine?: ShipmentLineSchemaLite | null;
  purchaseLine: PurchaseOrderLineCore | null;
  org: OrgLite;
  performedBy: UserLite | null;
  productionRun?: ProductionRunLite | null;
  purchaseProcessingOrg: string | null;
};

export type StockEventSchemaLite = Lite<StockEventSchema>;

type StockEventPOSTSchemaBase = POSTSchema<
  StockEventSchema,
  'eventType' | 'stockLot',
  | 'quant'
  | 'station'
  | 'testPassed'
  | 'mslFloorHours'
  | 'internalNotes'
  | 'externalNotes'
  | 'package'
  | 'stockLocation'
  | 'handlingRequest'
>;

type AllowedEventTypes = Exclude<
  StockEventType,
  StockEventType.SPLICE | StockEventType.COMBINE | StockEventType.SPLIT
>;

export type StockEventPOSTSchema = StockEventPOSTSchemaBase &
  (
    | {
        eventType: StockEventType.MOVE | StockEventType.RECEIVE;
        stockLocation: StockLocationSchemaLite['id'];
      }
    | {
        eventType: StockEventType.REPACKAGE;
        package: PackagingOptions;
      }
    | {
        eventType: StockEventType.ADJUST_QUANT;
        quant: number;
      }
    | {
        eventType: StockEventType.MSL_TRACK;
        mslFloorHours: number;
      }
    | {
        eventType:
          | StockEventType.INSPECTION_PHOTO
          | StockEventType.INSPECTION_PHOTO_SAMPLE
          | StockEventType.INSPECTION_PHOTO_FULL
          | StockEventType.X_RAY
          | StockEventType.X_RAY_SAMPLE
          | StockEventType.X_RAY_FULL
          | StockEventType.SOLDERABILITY
          | StockEventType.ANTI_COUNTERFEITING
          | StockEventType.INSPECTION_SURFACE
          | StockEventType.INSPECTION_DECAP
          | StockEventType.INSPECTION_3RD_PARTY
          | StockEventType.INSPECTION_DIMENSIONAL
          | StockEventType.INSPECTION_LCR;
        testPassed: boolean;
      }
    | {
        eventType: Exclude<
          AllowedEventTypes,
          | StockEventType.MOVE
          | StockEventType.RECEIVE
          | StockEventType.REPACKAGE
          | StockEventType.ADJUST_QUANT
          | StockEventType.INSPECTION_PHOTO
          | StockEventType.INSPECTION_PHOTO_SAMPLE
          | StockEventType.INSPECTION_PHOTO_FULL
          | StockEventType.X_RAY
          | StockEventType.X_RAY_SAMPLE
          | StockEventType.X_RAY_FULL
          | StockEventType.SOLDERABILITY
          | StockEventType.ANTI_COUNTERFEITING
          | StockEventType.INSPECTION_SURFACE
          | StockEventType.INSPECTION_DECAP
          | StockEventType.INSPECTION_3RD_PARTY
          | StockEventType.INSPECTION_DIMENSIONAL
          | StockEventType.INSPECTION_LCR
        >;
      }
  );

export type StockEventPOSTSplitSchema = {
  quant: number;
  stockLot: StockLotQuerySchema['id'];
  internalNotes?: string;
  externalNotes?: string;
  station?: string | null;
  split_unit?: UnitOfMeasureOptions;
  handlingRequest?: HandlingRequestSchema['id'] | null;
};

export type StockEventPOSTSpliceCombineSchema = {
  stockLots: StockLotQuerySchema['id'][];
  eventType: StockEventType.SPLICE | StockEventType.COMBINE;
  internalNotes?: string;
  externalNotes?: string;
  station?: string | null;
};

export type StockEventPOSTSplitResponseSchema = {
  createdStockEventIds: StockEventSchema['id'][];
  originalStockLotId: StockLotQuerySchema['id'];
  splitStockLotId: StockLotQuerySchema['id'];
};

export type StockEventPOSTSpliceCombineResponseSchema = {
  createdStockEventIds: StockEventSchema['id'][];
  originalStockLotIds: StockLotQuerySchema['id'][];
  mergedStockLotId: StockLotQuerySchema['id'];
};

export type StockEventUndoPOSTSchema = {
  stockLocation: StockLocation['id'];
};

type CommonReceivePOSTSchema = {
  quant?: number;
  dimensionClass?: DimensionClassOptions | null;
  dimensionUnit?: UnitOfMeasureOptions | null;
  dimensionValue?: number;
  stockLocation: StockLocation['id'];
  package?: PackagingOptions;
  countryOfOrigin?: string | null;
  dateCode?: string | null;
  mfgLot?: string | null;
  quantCertain?: boolean;
  quarantined?: boolean;
  smtNoPnp?: boolean;
  msl?: string | null;
  mslFloorHours?: number;
  internalNotes?: string | null;
  externalNotes?: string | null;
  tapeSize?: number;
  expiresAt?: string | null;
  station?: string | null;
  customProperties?: object;
  printer?: Printer['id'] | null;
  labelTemplate?: LabelTemplate['id'];
  additionalData?: object;
  weight?: number | null;
  height?: number | null;
  width?: number | null;
  length?: number | null;
  barcodeLabel?: BarcodeLabelSchema['id'] | null;
};

export type ReceivePurchaseLinePOSTSchema = CommonReceivePOSTSchema & {
  purchaseLine: PurchaseOrderLineCore['id'];
};

export type ReceiveShipmentLinePOSTSchema = CommonReceivePOSTSchema & {
  shipmentLine: ShipmentLineLite['id'];
};

type PartConfig = {
  part: Part['id'];
  v2OrgPart?: never;
  stockLot?: never;
};

type V2OrgPartConfig = {
  part?: never;
  v2OrgPart: OrgPartSchema['id'];
  stockLot?: never;
};

type StockLotConfig = {
  part?: never;
  v2OrgPart?: never;
  stockLot: StockLotQuerySchema['id'];
};

export type CreateReceiveShipmentLinePOSTSchema = CommonReceivePOSTSchema &
  (PartConfig | V2OrgPartConfig | StockLotConfig) & {
    shipment: Shipment['id'];
  };

export type PartialSplitStockLotPurchaseLinePOSTSchema = {
  quant: number;
  purchaseLine: PurchaseOrderLineCore['id'];
};
